import React, { useEffect, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import { EditorState, convertFromHTML, ContentState } from 'draft-js';
import { parseClasses, NSchange, validateImageURL,validateURL } from '../../lib/ep.js';

import { MainButton } from './ActionButton';
import ButtonCollection from './ButtonCollection.js';
import FieldErrorMessage from './FieldErrorMessage';
import FieldLabel from './FieldLabel';
import FloatingLayer from './FloatingLayer';
import SectionTitle from './SectionTitle';
import Tooltip from './Tooltip';

import SmartTagPicker from './SmartTagPicker';
import Loadable from 'react-loadable';
import LeveledHTMLText from '../front/helpers/LeveledHTMLText.js';
import UrlField from './UrlField.js';
import Model from './Model.js';

const EmojiPicker = Loadable({
	loader:() => import('./EmojiPicker'),
	loading:() => <div>Loading...</div>,
});

const RichEditor = (props) => {
	const input_ref = useRef();
	const emoji_picker = useRef();
	const smarttagpicker = useRef();
	var initialEditorState;

	const initial_name = (props.model != undefined) ? NSchange(props.model, props.name) : undefined;

	let initial_value = props.value ? props.value : ((initial_name != undefined) ? initial_name : '');

	if(initial_value != '' && initial_value != ' ' && initial_value != undefined) {
		const blocksFromHTML = convertFromHTML(initial_value);
		const contentState = ContentState.createFromBlockArray(blocksFromHTML);
		initialEditorState = EditorState.createWithContent(contentState);
	} else {
		initialEditorState = EditorState.createEmpty();
	}

	const [state,setState] = useState({
		init_value:initial_value,
		editorState:initialEditorState,
		mode:props?.mode ?? 'html',	// visual | html
		chars_left:false,
		has_many_chars:false,
		html_error:false,
		url:'',
		as_button:false,
	});

	const pile = [];
	let errors = [];

	const inputRef = (element) => {
		input_ref.current = element;
		if(props.inputRef !== undefined) props.inputRef(element);
	}

	const changeURL = (k, v) => {
		setState(state=>({ ...state, [k]:v }));
	}

	const getSelection = () => {
		let textVal = input_ref.current;

		return {
			start:textVal.selectionStart,
			end:textVal.selectionEnd,
		};
	}

	useEffect(() => {
		let _initial_value = (typeof state.init_value  === 'string') && state.init_value.replace(/\n+$/,"");	//Sanitize last newlines
		let content = sanitizeEventAttributes(_initial_value);
		let content_verified = true;
		content_verified = verifiedContent(content,'image');
		if(content_verified) content_verified = verifiedContent(content,'link');
		setState(state=>({...state, html_error:!content_verified ? props.intl.formatMessage({id:"The HTML is not valid"}) : false }));
	},[])
 
	const onFocus = (e) => {
		if(props.onFocus) props.onFocus(e);
	}

	const envelop = (text, pos, tag, extra, _class = null, _style = null) => {
		let stag = `<${tag}>`;
		let etag = `</${tag}>`;

		let selection = text.substring(pos.start,pos.end);
		let added_class = (_class) ? ` class="${_class}"`: ''
		let added_style = (_style) ? ` style="${_style}"`: ''

		switch(tag) {
			case 'ul':
			case 'ol':
				//If it's an unorderd we get the lines
				selection = selection.split("\n").map((i) => `<li>${i}</li>`).join("");
				break;
			
			case 'a':
				stag = `<a href="${extra}"${added_class}${added_style} target="_blank">`;
				break;

			case 'div':
				stag = `<div${added_class}${added_style}>\n`;
				etag = `\n</${tag}>`;
				break;
		}

		const ini = text.substring(0, pos.start);
		const end = text.substring(pos.end);
		return ini + stag + selection + etag + end;
	}

	const setContent = (content) => {
		if(props.change != undefined) props.change(props.name, content);
	}

	const change = (e, max_length) =>{
		errors = [];
		if(e.target.value == pile[pile.length - 1]) return;

		if(max_length) typeText(e, max_length);

		let content = sanitizeEventAttributes(e.target.value)
		
		let content_verified = true;
		content_verified = verifiedContent(content,'image');
		if(content_verified) content_verified = verifiedContent(content,'link');
		setState(state=>({...state, html_error:!content_verified ? props.intl.formatMessage({id:"The HTML is not valid"}) : false }));
		//Call the passed callback
		if(props.change != undefined) props.change(props.name, content);
		if(content != "" && content != pile[pile.length - 1]) pile.push(content);
	}

	const sanitizeEventAttributes = (content) => {
		const regex = /(\w+)\s*=\s*(['"]?)(.*?)\2\s*/g;
		return content.replace(regex, function(match, attributeName, quote, attributeValue) {
			if (attributeName.startsWith("on")) {
				return '';
			}
			return match;
		});
	}

	const verifiedContent = (content, type='') =>{

		let valid = true;
		// Expresión regular para buscar etiquetas de imágenes <img>
		
		const imgRegex = /<img\b[^>]*>/gi;
		const linkRegex = /<a\b[^>]*>/gi;

		const elementRegexp = type =='image' ? imgRegex : linkRegex;

		// Verificar cada etiqueta de imagen encontrada
		let sanitizedContent = content.replace(elementRegexp, (match) => {
			if(type == 'image'){
				// Verificar si la etiqueta de imagen tiene un atributo src válido
				const srcMatch = match.match(/src\s*=\s*(['"])(.*?)\1/i);
				if (srcMatch) {
					const src = srcMatch[2];
					const cleanSrc = src.replace(/^['"]|['"]$/g, '');
					if (validateImageURL(cleanSrc)) {
						// Si la URL es válida, devolver la etiqueta de imagen sin cambios
						return match;
					}
				}
				errors.push(props.intl.formatMessage({id:"The image {link} has an invalid URL."},{link:(srcMatch?.[0] ?? match).replace('src=','')}));
				// Si el atributo src no es válido o la URL no es válida, eliminar la etiqueta de imagen
				return '';
			}

			if(type == 'link'){
				const urlMatch = match.match(/href\s*=\s*(['"])(.*?)\1/i);
				const urlMatchAnchor = match.match(/href\s*=\s*(['"])(#[a-zA-Z0-9-_]+)\1/i);
				const urlMatchMailto = match.match(/href\s*=\s*(['"])mailto:([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\1/i);
				//const urlMatchSmartags = match.match(/href\s*=\s*(['"])\{\{(.*?)\}\}\1/i);//NO acceptava smartags als parametres
				const urlMatchSmartags = match.match(/href\s*=\s*['"][^'"]*\{\{.*?\}\}[^'"]*['"]/i);
				
				if (urlMatch) {
					if(urlMatchAnchor)return match;
					if(urlMatchSmartags)return match;//es un smartag, el donem per bo. Problemes a diccionaris
					if(urlMatchMailto)return match;//Es email amb format correcte

					const url = urlMatch[2];
					const cleanUrl = url.replace(/^['"]|['"]$/g, '');
					if (validateURL(cleanUrl)) {
						// Si la URL es válida, devolver la etiqueta de imagen sin cambios
						return match;
					}else{
						errors.push(props.intl.formatMessage({id:"The link {link} has an invalid URL."},{link:(urlMatch?.[0] ?? match).replace('href=','')}));
					}
				}else{
					errors.push(props.intl.formatMessage({id:"The link {link} is not well-formatted."},{link:match}));
				}
				// Si el atributo src no es válido o la URL no es válida, eliminar la etiqueta de imagen
				return '';
			}
		});
		// Asignar el contenido sanitizado al textarea
  		//textarea.value = sanitizedContent;
		valid = (sanitizedContent === content);
		if(!valid) return valid;
		return checkTags(content)
	}

	const checkTags = (text) => { 
		var tags = {};
		var i = 0;
		var j = 0;
		var k = 0;
		var tag = '';
	
		i = text.indexOf('<');
		while (i >= 0) {
			j = text.indexOf('>', i);
			if (j == -1) break;
			k = text.indexOf(' ', i);
			if (k > i && k < j) {
				tag = text.substr(i + 1, k - i - 1);
			} else {
				tag = text.substr(i + 1, j - i - 1);
			}
	
			if (tag.indexOf('/') == 0) {
				tag = tag.substr(1);
				if (tags[tag] == undefined) {
					tags[tag] = -1;
				} else {
					tags[tag]--;
				}
			} else {
				if (tags[tag] == undefined) {
					tags[tag] = 1;
				} else {
					tags[tag]++;
				}
			}
			i = text.indexOf('<', j);
		}
		
		var elementosAutonomos = ['br','hr','img','input','meta','link','area','base','col','command','embed','param','source','track','wbr','rect','circle','ellipse','line','polyline','polygon','path'];

		// Everything should be zero or negative
		for (tag in tags) {
			tag = tag.endsWith('/') ? tag.slice(0, -1) : tag;
			if (tags[tag] > 0 && !elementosAutonomos.includes(tag)){
				errors.push(props.intl.formatMessage({id:"The <{tag}> element is not closed correctly."},{tag}));
				return false;
			}
		}
	
		return true;
	};

	const keyPress = (e) => {
		if(e.key == 'z' && (e.metaKey || e.ctrlKey)) {
			e.preventDefault();
			setContent(pile.pop());
		}
	}

	const typeText = (e, max_length) => {
		if(!max_length) return;
		var input = e.target.value;
		var chars_left = max_length - input.length;
		setState(state => ({...state,chars_left:chars_left, has_many_chars:((chars_left < 0) ? true : false) }));
	}

	const toggleBlockType = (blockType) => {
		var c = getSelection();
		var content = envelop(input_ref.current.value, c, blockType);
		setContent(content);
	}

	const toggleAlignTypeWithStyle = (blockType, extra_style) => {
		setContent(envelop(input_ref.current.value, getSelection(), blockType, null, null, extra_style));
	}

	const promptForLink = (e) => {
		e.preventDefault();
		setState(state=>({...state,
			showURLInput:true,
			as_button:false,
			urlValue:'',
		}));
	}

	const promptForButton = (e) => {
		e.preventDefault();
		setState(state=>({...state,
			showURLInput:true,
			as_button:true,
			urlValue:'',
		}));
	}

	const confirmLink = (e) => {
		setContent(envelop(input_ref.current.value, getSelection(), 'a', state.url, state.as_button ? 'button' : false));
		setState(state=>({...state,
			showURLInput: false,
		}));
	}

	const concatText = (new_text) => {
		var pos = getSelection();
		var text = input_ref.current.value;
		let final = text.substring(0, pos.start) + new_text + text.substring(pos.end);
		setContent(final);
	}

	const { editorState } = state;
	const _styles = props?._style ?? {};
	const _field_styles = props?._field_style ?? {};

	const has_error = (props.errors && props.errors[props.name]) ? props.errors[props.name] : (state.html_error ? state.html_error : false);

	let className = 'RichEditor-editor_html';
	const disabled = props?.disabled ?? false;

	var classname = props?.classname ?? 'input';
	classname += ' RichEditor-root';
	if(disabled) classname += ' disabledItem';

	const _class_ = (props._class == undefined) ? { v_size:'normal' } : props._class;
	classname = parseClasses(classname, _class_, 'richeditor');

	const max_length = props?.max_length ?? null;
	const limitMaxLength = props?.limit_max_length ?? true;
	if(max_length) classname += ' with_charcounter';

	const has_emojis = props?.has_emojis ?? false;
	if(has_emojis) classname += ' with_emoji';

	const smarttags = props?.smarttags ?? false;
	const has_smarttags = (smarttags && smarttags.length > 0) ? true : false;
	if(has_smarttags) classname += ' with_smarttags';

	const display_wysiwyg_buttons = (has_emojis && props.display_wysiwyg_buttons != undefined && !props.display_wysiwyg_buttons) ? false : true;
	const enable_html = props?.enable_html ?? true;
	const enable_titles = props?.enable_titles ?? true;
	const enable_align = props?.enable_align ?? false;

	let field_class = ['common_form_item'];

	const tools = props?.tools ?? false;

	let urlInput;

	if(state.showURLInput) {
		urlInput =
			<FloatingLayer backButton={() => setState(state=>({...state,showURLInput:false}))} _class={{size:'small'}}>
				<div>
					<SectionTitle>{(state?.as_button ?? false) ? "HTML button manager" : "HTML link manager"}</SectionTitle>

					<Model model={state} change={changeURL}>
						<UrlField
							name='url'
							//onKeyAction={onLinkInputKeyDown}
							autofocus={true}
							_class={{ first:true }}
						>Enter your URL</UrlField>
					</Model>

					<ButtonCollection in_floating={true}>
						<MainButton
							icon='knk-on'
							onClick={confirmLink}
						>Confirm</MainButton>
					</ButtonCollection>
				</div>
			</FloatingLayer>;
	}

	return (
		<div class={'react_field' + ((props._field_class != undefined) ? ' ' + props._field_class : '')} style={_field_styles}>
			<div class={classname + ((has_error) ? ' has_error' : '')} style={_styles}>
				<FieldLabel
					{...props.label_props}
					name={props.name}
					autotranslate={props.autotranslate}
				>{props.children}</FieldLabel>

				<div class="field_container this_textarea is_rich textarea">
					{urlInput}
					<div class={className}>
						{(display_wysiwyg_buttons) && (
							<div class="gui_controls gui_flexbox_auto gui_flex_row gui_flex_nowrap">
								{(enable_html && enable_titles) && (
									<BlockStyleControls
										editorState={editorState}
										tools={tools}
										onToggle={toggleBlockType}
									/>
								)}

								{(enable_align) && (
									<AlignStyleControls
										editorState={editorState}
										tools={tools}
										onToggle={toggleAlignTypeWithStyle}
									/>
								)}

								{(enable_html) && (
									<InlineStyleControls
										editorState={editorState}
										tools={tools}
										onToggle={toggleBlockType}
										promptForLink={promptForLink}
										promptForButton={promptForButton}
										removeLink={()=>{}}
										add_button={props?.buttons ?? false}
									/>
								)}

								{(has_smarttags) && (
									<ul class="RichEditor-controls" style={{flexGrow:'1', justifyContent:'flex-end'}}>
										<li>
											<SmartTagPicker
												ref={smarttagpicker.current}
												tags={smarttags}
												wrapStart={props.wrapStart}
												wrapEnd={props.wrapEnd}
												onSmartTagPick={concatText}
											/>
										</li>
									</ul>
								)}
							</div>
						)}

						{(state.mode == 'visual') ? (
							<LeveledHTMLText
								_class={{font_size:'small_p', other:'div_textarea'}}
								contentEditable={true}
								ref={inputRef}
								onChange={change}
								onKeyDown={keyPress}
							>{initial_value}</LeveledHTMLText>
						) : (
							<textarea
								dir="auto"
								class={field_class.join(" ")}
								placeholder={(props.placeholder != undefined && props.placeholder != "") ? props.intl.formatMessage({id:props.placeholder}) : null}
								ref={inputRef}
								maxLength={(limitMaxLength) ? max_length : null}
								onChange={(e) => change(e, max_length)}
								value={initial_value}
								onKeyDown={keyPress}
								onFocus={onFocus}
								disabled={disabled}
								aria-label={props.name}
							/>
						)}

						{(has_emojis) && (
							<ul class="RichEditor-emoji">
								<li>
									<EmojiPicker
										ref={emoji_picker.current}
										onEmojiPick={concatText}
									/></li>
							</ul>
						)}
					</div>

					{(max_length) && (
						<div class="gui_charcounter_c">
							<div class={'is_monospaced gui_charcounter' + ((state.has_many_chars) ? ' redBg' : '')}>{(state.chars_left !== false) ? state.chars_left : max_length}</div>
						</div>
					)}

					{(props.after_component != undefined) && (
						<span class="label_additional_msg gui_after_message whole_contents">{props.after_component}</span>
					)}

					{(has_error) && (
						<FieldErrorMessage 
							error_msg_position_class={props.error_msg_position_class}
							>{has_error}
							{(errors.length > 0) ? <Tooltip position="top">{errors.join(" ")}</Tooltip> : null}
						</FieldErrorMessage>
					)}
				</div>
			</div>
		</div>
	);
	
}

const StyleButton = (props) => {
	const onToggle = (e) => {
		e.preventDefault();
		props.onToggle(props.style,props?._extra_style ?? null);
	};

	let _classes = ['RichEditor-style-button'];
	if(props.active) _classes.push('RichEditor-activeButton');
	if(props._class != undefined && props._class != '' && props._class) _classes.push(props._class);

	return (
		<span class={_classes.join(" ")} onMouseDown={onToggle}>
			<Tooltip with_icon={false} {...props.label} />
		</span>
	);
};

const ALIGN_TYPES = [
	{ label:"Align left", style:'div', extra_style:'text-align:left;', class:'knk-align-left' },
	{ label:"Align center", style:'div', extra_style:'text-align:center', class:'knk-align-center' },
	{ label:"Align right", style:'div', extra_style:'text-align:right', class:'knk-align-right' },
	{ label:"Justify", style:'div', extra_style:'text-align:justify', class:'knk-align-justified' },
];

const AlignStyleControls = (props) => {
	const { editorState } = props;
	const selection = editorState.getSelection();
	const blockType = editorState.getCurrentContent().getBlockForKey(selection.getStartKey()).getType();

	const types = ALIGN_TYPES.filter((b) => {
		if(props.tools == false) return true;

		for(var i in props.tools) {
			if(props.tools[i] == b.style) return true;
		}

		return false;
	});

	return (
		<ul className="RichEditor-controls">
			{types.map((type) => {
				return (
					<li key={type.label}>
						<StyleButton
							active={type.style === blockType}
							_class={type.class}
							_extra_style={type.extra_style}
							label={type.label}
							onToggle={props.onToggle}
							style={type.style}
						/>
					</li>
				)
			})}
		</ul>
	);
};


const BLOCK_TYPES = [
	{ label:"Heading 1", style:'h1', class:'knk-tag-h1' },
	{ label:"Heading 2", style:'h2', class:'knk-tag-h2' },
	{ label:"Heading 3", style:'h3', class:'knk-tag-h3' },
	{ label:"Paragraph", style:'p', class:'knk-tag-paragraph' },
	// { label:"H4", style:'header-four' },
	// { label:"H5", style:'header-five' },
	// { label:"H6", style:'header-six' },
	// { label:"Blockquote", style:'blockquote' },
	{ label:"Bulleted list", style:'ul', class:'knk-tag-bulleted-list' },
	{ label:"Ordered list", style:'ol', class:'knk-numbered-list' },
	// { label:"Code Block", style:'code-block' },
];

const BlockStyleControls = (props) => {
	const { editorState } = props;
	const selection = editorState.getSelection();
	const blockType = editorState.getCurrentContent().getBlockForKey(selection.getStartKey()).getType();

	const types = BLOCK_TYPES.filter((b) => {
		if(props.tools == false) return true;

		for(var i in props.tools) {
			if(props.tools[i] == b.style) return true;
		}

		return false;
	});

	return (
		<ul className="RichEditor-controls">
			{types.map((type) => {
				return (
					<li key={type.label}>
						<StyleButton
							active={type.style === blockType}
							_class={type.class}
							label={type.label}
							onToggle={props.onToggle}
							style={type.style}
						/>
					</li>
				)
			})}
		</ul>
	);
};

const INLINE_STYLES = [
	{ label:"Bold", style:'strong', class:'knk-tag-bold' },
	{ label:"Italic", style:'em', class:'knk-tag-italic' },
	// { label:"Underline", style:'UNDERLINE', class:'knk-edit' },
	// { label:"Monospace", style:'CODE' },
];

const InlineStyleControls = (props) => {
	var currentStyle = props.editorState.getCurrentInlineStyle();

	const types = INLINE_STYLES.filter((b) => {
		if(props.tools == false) return true;

		for(var i in props.tools) {
			if(props.tools[i] == b.style) return true;
		}

		return false;
	});

	return (
		<ul className="RichEditor-controls">
			{types.map(type =>
				<li key={type.label}>
					<StyleButton
						active={currentStyle.has(type.style)}
						_class={type.class}
						label={type.label}
						onToggle={props.onToggle}
						style={type.style}
					/>
				</li>
			)}

			<li>
				<span class="RichEditor-style-button knk-links" onMouseDown={props.promptForLink}>
					<Tooltip with_icon={false} position="top">Link</Tooltip>
				</span>
			</li>
			{(props?.add_button ?? false) && (
				<li>
					<span class="RichEditor-style-button knk-mouseover" onMouseDown={props.promptForButton}>
						<Tooltip with_icon={false} position="top">Button</Tooltip>
					</span>
				</li>
			)}
		</ul>
	);
};

export default injectIntl(RichEditor);